import { useEffect, useRef, useState } from 'react'
import { gsap, Flip, prefersReduced } from '../../lib/gsap.js'
import { indexFromMidpoints } from '../../lib/format.js'
import useIso from './useIso.js'

const DRAG_CLEAR = 'x,y,scale,zIndex,position,top,left,width,margin,pointerEvents,boxShadow'

// Pure: from a DOM node, find the nearest ancestor carrying data-drop-col.
export function dropColFromPoint(el) {
  let node = el
  while (node) {
    const id = node.getAttribute ? node.getAttribute('data-drop-col') : null
    if (id != null) return id
    node = node.parentElement
  }
  return null
}

// Unified mouse + touch board drag via Pointer Events.
// Fine pointers (mouse) drag after a 4px move; coarse pointers (touch) require a
// long-press so the board can still be scrolled. onDrop(itemId, colId) fires on
// release over a resolved column. The dragged card lifts to position: fixed and
// follows the pointer via gsap.quickSetter (fixed so it escapes the column's
// overflow-y: auto clip; pointer-events: none so elementFromPoint reads the
// column underneath it).
export default function useBoardDrag({ onDrop, orderSignature = '', longPressMs = 200 }) {
  const [draggingId, setDraggingId] = useState(null)
  const [overCol, setOverCol] = useState(null)
  const state = useRef({
    id: null, armed: false, timer: null, startX: 0, startY: 0, pointerId: null,
    el: null, setX: null, setY: null, pendingDrops: new Map(),
  })
  const didDrag = useRef(false)
  const armRef = useRef(() => {})
  const containerRef = useRef(null)
  const lastSig = useRef(orderSignature)
  const captured = useRef(null)

  // Capture the "before" state during render — runs before React mutates the
  // DOM, so it reads the current/old layout (including the dragged card still
  // pinned at its drop position). Read-only DOM read in render — intentional.
  if (orderSignature !== lastSig.current) {
    lastSig.current = orderSignature
    const c = containerRef.current
    if (c) captured.current = Flip.getState(c.querySelectorAll('[data-flip-id]'))
  }

  useEffect(() => {
    const s = state.current
    const clearTimer = () => { if (s.timer) { clearTimeout(s.timer); s.timer = null } }
    const releaseCard = (el) => { if (el) gsap.set(el, { clearProps: DRAG_CLEAR }) }
    // The lifted card is pointer-events:none, so a held-button drag sweeps over
    // the cards underneath and text-selects them. Suppress selection while a drag
    // is active; restore on release.
    const lockSelection = () => {
      document.body.style.userSelect = 'none'
      document.body.style.webkitUserSelect = 'none'
      const sel = window.getSelection && window.getSelection()
      if (sel) sel.removeAllRanges()
    }
    const unlockSelection = () => {
      document.body.style.userSelect = ''
      document.body.style.webkitUserSelect = ''
    }
    const computeIndex = (colEl, pointerY, draggedId) => {
      if (!colEl) return null
      const body = colEl.querySelector('.cn-board-col-body') || colEl
      const mids = Array.from(body.querySelectorAll('[data-flip-id]'))
        .filter((c) => c.getAttribute('data-flip-id') !== String(draggedId))
        .map((c) => { const r = c.getBoundingClientRect(); return r.top + r.height / 2 })
      return indexFromMidpoints(mids, pointerY)
    }

    const arm = () => {
      s.armed = true
      setDraggingId(s.id)
      lockSelection()
      const el = s.el
      if (!el) return
      const r = el.getBoundingClientRect()
      gsap.set(el, { position: 'fixed', top: r.top, left: r.left, width: r.width, margin: 0, zIndex: 2000, pointerEvents: 'none' })
      s.setX = gsap.quickSetter(el, 'x', 'px')
      s.setY = gsap.quickSetter(el, 'y', 'px')
      if (!prefersReduced()) gsap.to(el, { scale: 1.03, boxShadow: '0 14px 32px rgba(0,0,0,.4)', duration: 0.14 })
    }
    armRef.current = arm

    const finish = (commit, e) => {
      clearTimer()
      didDrag.current = s.armed
      if (s.armed && commit && e) {
        const under = document.elementFromPoint(e.clientX, e.clientY)
        const colEl = under && under.closest ? under.closest('[data-drop-col]') : null
        const col = colEl ? colEl.getAttribute('data-drop-col') : null
        if (col != null && s.id != null) {
          const index = computeIndex(colEl, e.clientY, s.id)
          // Keep the card pinned at the drop point so the Flip effect can capture
          // it there; register per-element so a rapid second drop can never orphan
          // the first. The Flip effect clears it when the reorder actually lands.
          const dropped = s.el
          onDrop(s.id, col, index)
          const tid = setTimeout(() => {
            if (state.current.pendingDrops.has(dropped)) {
              gsap.set(dropped, { clearProps: DRAG_CLEAR })
              state.current.pendingDrops.delete(dropped)
            }
          }, 1500)                 // last-resort safety only (genuine same-slot no-op where orderSignature never changes); real moves are cleared by the Flip effect
          s.pendingDrops.set(dropped, tid)
        } else {
          releaseCard(s.el)        // dropped on no column -> just clear
        }
      } else {
        releaseCard(s.el)          // aborted/cancelled -> just clear
      }
      s.id = null; s.armed = false; s.el = null; s.setX = null; s.setY = null
      setDraggingId(null); setOverCol(null)
      unlockSelection()
    }

    const onMove = (e) => {
      if (s.id == null) return
      // Ignore a second finger/pointer moving while we're tracking a different one.
      if (e.pointerId !== s.pointerId) return
      if (!s.armed) {
        const dx = Math.abs(e.clientX - s.startX)
        const dy = Math.abs(e.clientY - s.startY)
        if (e.pointerType !== 'touch' && (dx > 4 || dy > 4)) { clearTimer(); arm() }
        else if (e.pointerType === 'touch' && (dx > 10 || dy > 10)) {
          // Finger moved before the long-press armed: this is a scroll, not a
          // drag attempt. Cancel the pending timer and give up so touch scroll
          // still works.
          clearTimer()
          s.id = null
          return
        }
        else return
      }
      e.preventDefault()
      if (s.setX) { s.setX(e.clientX - s.startX); s.setY(e.clientY - s.startY) }
      const under = document.elementFromPoint(e.clientX, e.clientY)
      const colEl = under && under.closest ? under.closest('[data-drop-col]') : null
      setOverCol(colEl ? colEl.getAttribute('data-drop-col') : null)
    }
    const onUp = (e) => {
      if (s.id != null && e.pointerId !== s.pointerId) return
      finish(true, e)
    }
    const onCancel = (e) => {
      if (s.id != null && e.pointerId !== s.pointerId) return
      finish(false, null)
    }
    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
    return () => {
      clearTimer()
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
    }
  }, [onDrop])

  // Unmount-only timer cleanup. MUST NOT live in the [onDrop] effect: onDrop's
  // identity changes on every Firestore snapshot (tasks/deals are new arrays), so
  // that effect tears down constantly. Clearing pendingDrops there would cancel a
  // mid-drop card's safety timer without clearing its pin — stranding the card
  // forever when an unrelated field (not in orderSignature) changes. Empty deps =>
  // this cleanup runs only on true unmount, where the board DOM is gone anyway.
  useEffect(() => () => {
    state.current.pendingDrops.forEach((tid) => clearTimeout(tid))
    state.current.pendingDrops.clear()
  }, [])

  // Animate after commit — runs once the snapshot re-render lands.
  useIso(() => {
    const cap = captured.current
    captured.current = null
    if (!cap) return
    const s = state.current
    // Any cards left pinned (position:fixed) at their drop point were captured
    // there; clear them now — confirmation-driven — so they settle into their
    // real new slots. Clearing ALL pending drops here means a rapid second drop
    // can never orphan the first (both clear on the next sig change; never stuck).
    if (s.pendingDrops.size) {
      s.pendingDrops.forEach((tid, el) => { clearTimeout(tid); gsap.set(el, { clearProps: DRAG_CLEAR }) })
      s.pendingDrops.clear()
    }
    if (prefersReduced()) return
    Flip.from(cap, {
      duration: 0.35,
      ease: 'power2.out',
      absolute: true,
      onEnter: (els) => gsap.fromTo(els, { opacity: 0 }, { opacity: 1, duration: 0.2 }),
      onLeave: (els) => gsap.to(els, { opacity: 0, duration: 0.2 }),
    })
  }, [orderSignature])

  const getCardHandlers = (itemId) => ({
    'data-flip-id': itemId,
    onPointerDown: (e) => {
      const s = state.current
      // Track the pointerId that started this drag so a second touch (multi-
      // touch) can't move/commit/cancel a drag it didn't start.
      s.id = itemId; s.armed = false; s.startX = e.clientX; s.startY = e.clientY
      s.pointerId = e.pointerId; s.el = e.currentTarget
      if (e.pointerType === 'touch') {
        if (s.timer) clearTimeout(s.timer)
        s.timer = setTimeout(() => armRef.current(), longPressMs)
      }
    },
  })
  const getColProps = (colId) => ({
    'data-drop-col': colId,
    'data-drop-over': overCol === colId ? 'true' : 'false',
  })
  const wasDragging = () => {
    const v = didDrag.current
    didDrag.current = false
    return v
  }
  return { draggingId, overCol, containerRef, getCardHandlers, getColProps, wasDragging }
}
